import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
  addEdge, Background, Controls, MiniMap,
  useNodesState, useEdgesState, Handle, Position,
  MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';
import { studioAdminApi as adminApi } from '../../api/studioClient';
import toast from 'react-hot-toast';
import { Plus, Trash2, Save, Clock, User, AlertTriangle, X, Maximize2, Minimize2 } from 'lucide-react';

const PRIORITY_COLORS = {
  urgent: { bg: '#FEE2E2', border: '#EF4444', text: '#B91C1C' },
  high: { bg: '#FFEDD5', border: '#F97316', text: '#C2410C' },
  normal: { bg: '#DBEAFE', border: '#3B82F6', text: '#1D4ED8' },
  low: { bg: '#F3F4F6', border: '#9CA3AF', text: '#4B5563' },
};

const CONDITION_LABELS = {
  completed: '✓ On Complete',
  cancelled: '✗ On Cancel',
  any: '↺ Any Status',
};

const CONDITION_COLORS = {
  completed: '#22C55E',
  cancelled: '#EF4444',
  any: '#8B5CF6',
};

// Custom Task Node
function TaskNode({ data, selected }) {
  const pc = PRIORITY_COLORS[data.priority] || PRIORITY_COLORS.normal;
  // Inputs = ▲ green triangles pointing up, Outputs = ▼ indigo triangles pointing down
  const inputStyle = {
    background: '#10B981', width: 12, height: 12, border: 'none',
    borderRadius: 0, clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
  };
  const outputStyle = {
    background: '#6366F1', width: 12, height: 12, border: 'none',
    borderRadius: 0, clipPath: 'polygon(0% 0%, 100% 0%, 50% 100%)',
  };
  return (
    <div className="relative" style={{ minWidth: 220 }}>
      {/* Input on left ◄ green */}
      <Handle type="target" position={Position.Left} id="in"
        style={{ ...inputStyle, top: '50%', transform: 'translateY(-50%) rotate(-90deg)' }} />
      {/* Output on right ► indigo */}
      <Handle type="source" position={Position.Right} id="out"
        style={{ ...outputStyle, top: '50%', transform: 'translateY(-50%) rotate(-90deg)' }} />
      <div style={{
        background: 'white', borderRadius: 10, border: `2px solid ${selected ? '#6366F1' : pc.border}`,
        boxShadow: selected ? '0 0 0 3px rgba(99,102,241,0.2)' : '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden', transition: 'all 0.15s'
      }}>
        {/* Header */}
        <div style={{ background: pc.bg, padding: '8px 12px', borderBottom: `1px solid ${pc.border}30` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: pc.text }}>{data.label}</span>
            <span style={{
              fontSize: 9, padding: '1px 6px', borderRadius: 4,
              background: pc.border + '20', color: pc.text, fontWeight: 600, textTransform: 'uppercase'
            }}>{data.priority}</span>
          </div>
        </div>
        {/* Body */}
        <div style={{ padding: '8px 12px' }}>
          {data.description && (
            <p style={{ fontSize: 10, color: '#6B7280', margin: '0 0 6px 0', lineHeight: '1.3' }}>{data.description}</p>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {data.dependency_mode === 'any' && (
              <span style={{ fontSize: 9, color: '#F97316', fontWeight: 700, background: '#FFF7ED', padding: '1px 5px', borderRadius: 4 }}>OR</span>
            )}
            {data.due_days > 0 && (
              <span style={{ fontSize: 9, color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: 2 }}>
                <Clock style={{ width: 10, height: 10 }} /> +{data.due_days}d
              </span>
            )}
            {data.default_assignee_role && (
              <span style={{ fontSize: 9, color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: 2 }}>
                <User style={{ width: 10, height: 10 }} /> {data.default_assignee_role}
              </span>
            )}
            {data.category && data.category !== 'general' && (
              <span style={{ fontSize: 9, color: '#9CA3AF' }}>{data.category}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const nodeTypes = { taskNode: TaskNode };

// Edge with condition label
function getEdgeStyle(condition) {
  const color = CONDITION_COLORS[condition] || CONDITION_COLORS.completed;
  return {
    stroke: color, strokeWidth: 3,
    markerEnd: { type: MarkerType.ArrowClosed, color }
  };
}

// Edit panel for selected node
function NodeEditPanel({ node, onUpdate, onDelete, onClose }) {
  const [form, setForm] = useState({
    title: node.data.label, description: node.data.description || '',
    priority: node.data.priority, category: node.data.category || 'general',
    due_days: node.data.due_days || 0, default_assignee_role: node.data.default_assignee_role || '',
    dependency_mode: node.data.dependency_mode || 'all',
  });
  const PRIORITIES = ['low', 'normal', 'high', 'urgent'];
  const CATEGORIES = ['general', 'admin', 'medical', 'legal', 'investigation', 'client_comm', 'discovery', 'settlement'];
  const ROLES = ['', 'attorney', 'paralegal', 'legal_assistant', 'case_manager'];

  return (
    <div style={{
      position: 'absolute', right: 12, top: 12, width: 280, background: 'white',
      borderRadius: 12, border: '1px solid #E5E7EB', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      zIndex: 10, overflow: 'hidden'
    }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>Edit Task</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}><X style={{ width: 16, height: 16 }} /></button>
      </div>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 500, color: '#6B7280', display: 'block', marginBottom: 3 }}>Title</label>
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
            style={{ width: '100%', padding: '6px 10px', fontSize: 12, border: '1px solid #D1D5DB', borderRadius: 6, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 500, color: '#6B7280', display: 'block', marginBottom: 3 }}>Description</label>
          <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
            style={{ width: '100%', padding: '6px 10px', fontSize: 12, border: '1px solid #D1D5DB', borderRadius: 6, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 500, color: '#6B7280', display: 'block', marginBottom: 3 }}>Priority</label>
            <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
              style={{ width: '100%', padding: '6px 8px', fontSize: 11, border: '1px solid #D1D5DB', borderRadius: 6, boxSizing: 'border-box' }}>
              {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 500, color: '#6B7280', display: 'block', marginBottom: 3 }}>Due Days</label>
            <input type="number" min={0} value={form.due_days} onChange={e => setForm({ ...form, due_days: parseInt(e.target.value) || 0 })}
              style={{ width: '100%', padding: '6px 8px', fontSize: 11, border: '1px solid #D1D5DB', borderRadius: 6, boxSizing: 'border-box' }} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 500, color: '#6B7280', display: 'block', marginBottom: 3 }}>Category</label>
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
              style={{ width: '100%', padding: '6px 8px', fontSize: 11, border: '1px solid #D1D5DB', borderRadius: 6, boxSizing: 'border-box' }}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 500, color: '#6B7280', display: 'block', marginBottom: 3 }}>Assignee Role</label>
            <select value={form.default_assignee_role} onChange={e => setForm({ ...form, default_assignee_role: e.target.value })}
              style={{ width: '100%', padding: '6px 8px', fontSize: 11, border: '1px solid #D1D5DB', borderRadius: 6, boxSizing: 'border-box' }}>
              {ROLES.map(r => <option key={r} value={r}>{r ? r.replace('_', ' ') : 'Unassigned'}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 500, color: '#6B7280', display: 'block', marginBottom: 3 }}>Dependency Mode (merge logic)</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setForm({ ...form, dependency_mode: 'all' })}
              style={{ flex: 1, padding: '5px 8px', fontSize: 11, fontWeight: 600, borderRadius: 6, cursor: 'pointer', border: '2px solid', transition: 'all 0.15s',
                background: form.dependency_mode === 'all' ? '#EEF2FF' : 'white',
                borderColor: form.dependency_mode === 'all' ? '#6366F1' : '#D1D5DB',
                color: form.dependency_mode === 'all' ? '#4338CA' : '#6B7280' }}>
              AND — All deps
            </button>
            <button onClick={() => setForm({ ...form, dependency_mode: 'any' })}
              style={{ flex: 1, padding: '5px 8px', fontSize: 11, fontWeight: 600, borderRadius: 6, cursor: 'pointer', border: '2px solid', transition: 'all 0.15s',
                background: form.dependency_mode === 'any' ? '#FFF7ED' : 'white',
                borderColor: form.dependency_mode === 'any' ? '#F97316' : '#D1D5DB',
                color: form.dependency_mode === 'any' ? '#C2410C' : '#6B7280' }}>
              OR — Any dep
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #F3F4F6' }}>
          <button onClick={() => onDelete(node.id)}
            style={{ fontSize: 11, color: '#EF4444', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Trash2 style={{ width: 12, height: 12 }} /> Delete
          </button>
          <button onClick={() => onUpdate(node.id, form)}
            style={{ fontSize: 11, fontWeight: 600, color: 'white', background: '#6366F1', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer' }}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// Edge condition editor
function EdgeEditPanel({ edge, onUpdate, onDelete, onClose, pos }) {
  const [condition, setCondition] = useState(edge.data?.condition || 'completed');
  return (
    <div style={{
      position: 'fixed', left: pos.x + 10, top: pos.y - 80, width: 240, background: 'white',
      borderRadius: 12, border: '1px solid #E5E7EB', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      zIndex: 10, overflow: 'hidden'
    }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>Edge Condition</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}><X style={{ width: 14, height: 14 }} /></button>
      </div>
      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {Object.entries(CONDITION_LABELS).map(([key, label]) => (
          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12 }}>
            <input type="radio" checked={condition === key} onChange={() => setCondition(key)} />
            <span style={{ color: CONDITION_COLORS[key], fontWeight: 500 }}>{label}</span>
          </label>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #F3F4F6' }}>
          <button onClick={() => onDelete(edge.id)}
            style={{ fontSize: 11, color: '#EF4444', background: 'none', border: 'none', cursor: 'pointer' }}>Remove</button>
          <button onClick={() => onUpdate(edge.id, condition)}
            style={{ fontSize: 11, fontWeight: 600, color: 'white', background: '#6366F1', border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TaskFlowEditor({ workflowId, stageId, stageName }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [edgeClickPos, setEdgeClickPos] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const templatesRef = useRef([]);

  // Load templates and build nodes/edges
  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getTaskTemplates(workflowId, stageId);
      const templates = res.data.templates;
      templatesRef.current = templates;

      const newNodes = templates.map((t, i) => ({
        id: t.id,
        type: 'taskNode',
        position: { x: t.position_x || (i % 3) * 260 + 50, y: t.position_y || Math.floor(i / 3) * 180 + 50 },
        data: {
          label: t.title, description: t.description, priority: t.priority || 'normal',
          category: t.category, due_days: t.due_days, default_assignee_role: t.default_assignee_role,
          templateId: t.id, dependency_mode: t.dependency_mode || 'all',
        },
      }));

      // Build edges from depends_on and edge_conditions
      const newEdges = [];
      const edgeSet = new Set();
      for (const t of templates) {
        const conditions = t.edge_conditions || [];
        for (const ec of conditions) {
          const key = ec.from + '->' + t.id;
          if (!edgeSet.has(key)) {
            edgeSet.add(key);
            newEdges.push({
              id: 'e-' + ec.from + '-' + t.id,
              source: ec.from, target: t.id,
              data: { condition: ec.condition || 'completed' },
              label: CONDITION_LABELS[ec.condition || 'completed'],
              labelStyle: { fontSize: 9, fontWeight: 600, fill: CONDITION_COLORS[ec.condition || 'completed'] },
              ...getEdgeStyle(ec.condition || 'completed'),
              animated: ec.condition === 'cancelled',
            });
          }
        }
        // Also build from depends_on if no edge_conditions
        for (const depId of (t.depends_on || [])) {
          const key = depId + '->' + t.id;
          if (!edgeSet.has(key)) {
            edgeSet.add(key);
            newEdges.push({
              id: 'e-' + depId + '-' + t.id,
              source: depId, target: t.id,
              data: { condition: 'completed' },
              label: CONDITION_LABELS.completed,
              labelStyle: { fontSize: 9, fontWeight: 600, fill: CONDITION_COLORS.completed },
              ...getEdgeStyle('completed'),
            });
          }
        }
      }

      setNodes(newNodes);
      setEdges(newEdges);
    } catch (e) { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  }, [workflowId, stageId]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // Escape key exits fullscreen
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape' && fullscreen) setFullscreen(false); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [fullscreen]);

  // Connect two nodes
  const onConnect = useCallback((params) => {
    const condition = 'completed';
    const newEdge = {
      ...params,
      id: 'e-' + params.source + '-' + params.target,
      data: { condition },
      label: CONDITION_LABELS[condition],
      labelStyle: { fontSize: 9, fontWeight: 600, fill: CONDITION_COLORS[condition] },
      ...getEdgeStyle(condition),
    };
    setEdges(eds => addEdge(newEdge, eds));
  }, []);

  // Add new task node
  const handleAddTask = async () => {
    const count = nodes.length;
    try {
      const res = await adminApi.createTaskTemplate(workflowId, stageId, {
        title: 'New Task ' + (count + 1),
        priority: 'normal',
        position_x: (count % 3) * 260 + 50,
        position_y: Math.floor(count / 3) * 180 + 50,
      });
      const t = res.data.template;
      setNodes(nds => [...nds, {
        id: t.id,
        type: 'taskNode',
        position: { x: t.position_x || 50, y: t.position_y || 50 },
        data: {
          label: t.title, description: t.description, priority: t.priority || 'normal',
          category: t.category, due_days: t.due_days, default_assignee_role: t.default_assignee_role,
          templateId: t.id, dependency_mode: t.dependency_mode || 'all',
        },
      }]);
      toast.success('Task added');
    } catch (e) { toast.error('Failed to add task'); }
  };

  // Update node data
  const handleNodeUpdate = async (nodeId, form) => {
    try {
      await adminApi.updateTaskTemplate(workflowId, stageId, nodeId, {
        title: form.title, description: form.description, priority: form.priority,
        category: form.category, due_days: form.due_days, default_assignee_role: form.default_assignee_role,
        dependency_mode: form.dependency_mode,
      });
      setNodes(nds => nds.map(n => n.id === nodeId ? {
        ...n, data: {
          ...n.data, label: form.title, description: form.description,
          priority: form.priority, category: form.category,
          due_days: form.due_days, default_assignee_role: form.default_assignee_role,
          dependency_mode: form.dependency_mode,
        }
      } : n));
      setSelectedNode(null);
      toast.success('Task updated');
    } catch (e) { toast.error('Failed to update'); }
  };

  // Delete node
  const handleNodeDelete = async (nodeId) => {
    if (!window.confirm('Delete this task template?')) return;
    try {
      await adminApi.deleteTaskTemplate(workflowId, stageId, nodeId);
      setNodes(nds => nds.filter(n => n.id !== nodeId));
      setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
      setSelectedNode(null);
      toast.success('Task deleted');
    } catch (e) { toast.error('Failed to delete'); }
  };

  // Update edge condition
  const handleEdgeUpdate = (edgeId, condition) => {
    setEdges(eds => eds.map(e => e.id === edgeId ? {
      ...e, data: { ...e.data, condition },
      label: CONDITION_LABELS[condition],
      labelStyle: { fontSize: 9, fontWeight: 600, fill: CONDITION_COLORS[condition] },
      ...getEdgeStyle(condition),
      animated: condition === 'cancelled',
    } : e));
    setSelectedEdge(null);
  };

  // Delete edge
  const handleEdgeDelete = (edgeId) => {
    setEdges(eds => eds.filter(e => e.id !== edgeId));
    setSelectedEdge(null);
  };

  // Save all positions and dependencies
  const handleSaveAll = async () => {
    setSaving(true);
    try {
      for (const node of nodes) {
        // Gather incoming edges for this node
        const incomingEdges = edges.filter(e => e.target === node.id);
        const depends_on = incomingEdges.filter(e => (e.data?.condition || 'completed') === 'completed').map(e => e.source);
        const edge_conditions = incomingEdges.map(e => ({
          from: e.source, condition: e.data?.condition || 'completed'
        }));

        await adminApi.updateTaskTemplate(workflowId, stageId, node.id, {
          position_x: node.position.x, position_y: node.position.y,
          depends_on, edge_conditions,
          dependency_mode: node.data.dependency_mode || 'all',
        });
      }
      toast.success('Flow saved');
    } catch (e) { toast.error('Failed to save flow'); }
    finally { setSaving(false); }
  };

  const onNodeClick = useCallback((e, node) => {
    setSelectedNode(node);
    setSelectedEdge(null);
  }, []);

  const onEdgeClick = useCallback((e, edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
    setEdgeClickPos({ x: e.clientX, y: e.clientY });
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
  }, []);

  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>Loading task flow...</div>;

  return (
    <div style={fullscreen ? {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
      background: '#FAFAFA', overflow: 'hidden'
    } : {
      position: 'relative', height: 500, border: '1px solid #E5E7EB', borderRadius: 12,
      overflow: 'hidden', background: '#FAFAFA'
    }}>
      {/* Toolbar */}
      <div style={{
        position: 'absolute', top: 12, left: 12, zIndex: 10, display: 'flex', gap: 8,
      }}>
        <button onClick={handleAddTask}
          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', fontSize: 11, fontWeight: 600,
            background: '#6366F1', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <Plus style={{ width: 14, height: 14 }} /> Add Task
        </button>
        <button onClick={handleSaveAll} disabled={saving}
          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', fontSize: 11, fontWeight: 600,
            background: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: 8, cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            opacity: saving ? 0.6 : 1 }}>
          <Save style={{ width: 14, height: 14 }} /> {saving ? 'Saving...' : 'Save Flow'}
        </button>
        <button onClick={() => setFullscreen(f => !f)}
          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', fontSize: 11, fontWeight: 600,
            background: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: 8, cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          {fullscreen ? <Minimize2 style={{ width: 14, height: 14 }} /> : <Maximize2 style={{ width: 14, height: 14 }} />}
          {fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
        </button>
      </div>
      {/* Stage label */}
      <div style={{ position: 'absolute', top: 14, right: 300, zIndex: 5, fontSize: 11, color: '#9CA3AF' }}>
        {stageName} — {nodes.length} tasks
      </div>
      {/* Flow canvas */}
      <ReactFlow
        nodes={nodes} edges={edges}
        onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick} onEdgeClick={onEdgeClick} onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        fitView fitViewOptions={{ padding: 0.2 }}
        snapToGrid snapGrid={[15, 15]}
        deleteKeyCode="Delete"
      >
        <Background variant="dots" gap={15} size={1} color="#E5E7EB" />
        <Controls style={{ bottom: 12, left: 12 }} />
        <MiniMap nodeColor={(n) => {
          const pc = PRIORITY_COLORS[n.data?.priority] || PRIORITY_COLORS.normal;
          return pc.border;
        }} style={{ bottom: 12, right: 12, width: 120, height: 80 }} />
      </ReactFlow>
      {/* Edit panels */}
      {selectedNode && (
        <NodeEditPanel node={selectedNode} onUpdate={handleNodeUpdate}
          onDelete={handleNodeDelete} onClose={() => setSelectedNode(null)} />
      )}
      {selectedEdge && (
        <EdgeEditPanel edge={selectedEdge} onUpdate={handleEdgeUpdate}
          onDelete={handleEdgeDelete} onClose={() => setSelectedEdge(null)} pos={edgeClickPos} />
      )}
    </div>
  );
}
